import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import { TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  BUTTON_CANCEL,
  BUTTON_REPLACE,
  BUTTON_START_OVER,
  REPLACE_TITLE,
  START_OVER_TITLE,
} from "./copy";

interface ReplaceAutoAnnotationsDialogProps {
  open: boolean;
  /** Unapproved regions on the canvas, of any geometry — a run replaces them all. */
  stagedCount: number;
  /** Turns of the Robopipe Vision conversation a fresh run discards, or 0. */
  discardsTurns: number;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Asked before an auto-annotate run that would throw something away: the
 * image's staged (unapproved) regions, which the server replaces as a whole
 * set, and/or the Robopipe Vision conversation a fresh run starts over.
 */
export const ReplaceAutoAnnotationsDialog = ({
  open,
  stagedCount,
  discardsTurns,
  onConfirm,
  onCancel,
}: ReplaceAutoAnnotationsDialogProps) => {
  const { t } = useTranslation(["label", "common"]);
  const replacing = stagedCount > 0;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <div className="flex flex-row items-center gap-2">
            <TriangleAlert className="size-5 text-amber-500" />
            <DialogTitle>{replacing ? t(REPLACE_TITLE) : t(START_OVER_TITLE)}</DialogTitle>
          </div>
          <DialogDescription>
            {replacing
              ? t("replaceDialog.replaceBody", { count: stagedCount })
              : t("replaceDialog.startOverBody", { count: discardsTurns })}
            {replacing &&
              discardsTurns > 0 &&
              ` ${t("replaceDialog.conversationNote", { count: discardsTurns })}`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onCancel}>
            {t(BUTTON_CANCEL)}
          </Button>
          <Button onClick={onConfirm}>
            {replacing ? t(BUTTON_REPLACE) : t(BUTTON_START_OVER)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
